import { Observable, of, combineLatest } from 'rxjs';
import { map, tap } from 'rxjs/operators/';
// Services.
import { StorageService } from '../storage/storage.service';
import { FetchService } from '../fetch/fetch.service';
// Services.
import { LoggerService } from '../logger/logger.service';
// Classes.
import { Logger } from '../../classes/logger';
import { Article } from '../../classes/article';
import { IndexItem } from '../../classes/indexItem';
import { RBImage } from '../../classes/image';
import { SearchResults } from '../../classes/searchResults';
import * as i0 from "@angular/core";
import * as i1 from "../storage/storage.service";
import * as i2 from "../fetch/fetch.service";
import * as i3 from "../logger/logger.service";
class RefToken {
    constructor(id, label, type) {
        this.id = id;
        this.label = label;
        this.type = type;
    }
}
export class DataService {
    constructor(storage, fetcher, loggerSrvc) {
        this.storage = storage;
        this.fetcher = fetcher;
        this.logger = new Logger(loggerSrvc, this.constructor.name);
    }
    // Index methods.
    /////////////////
    /**
     * Index.
     */
    getIndex(id) {
        // Load index item from cache.
        let indexItem = this.storage.getIndexItem(id);
        if (indexItem) {
            this.logger.log(`Return index "${id}" from storage`, indexItem);
            return of(indexItem);
        }
        else {
            // Load index from api.
            this.logger.log(`Fetch index "${id}" from server`, indexItem);
            return this.fetcher.getIndex(id).pipe(map(rawIndex => this.processRawIndexItem(rawIndex)));
        }
    }
    processRawIndexItem(rawIndexItem) {
        return this.processRawIndexItemRecursive(rawIndexItem);
    }
    processRawIndexItemRecursive(rawIndexItem) {
        let indexItem = null;
        if (rawIndexItem) {
            indexItem = new IndexItem(rawIndexItem);
            // Process children.
            indexItem.children = rawIndexItem.children.map((indexItemChild) => this.processRawIndexItemRecursive(indexItemChild));
            // Store index item.
            this.storage.putIndexItem(indexItem);
        }
        return indexItem;
    }
    // Article methods.
    ///////////////////
    getArticle(id) {
        // Load article from api.
        return this.fetcher.getArticle(id).pipe(map(rawArticle => this.processRawArticle(rawArticle)));
    }
    processRawArticle(rawArticle) {
        let imageIds = {};
        let articles = {};
        let article = this.processRawArticleRecursive(rawArticle, imageIds, articles);
        // Add article to all created articles array.
        articles[article.id] = article;
        // Request all images.
        let imageIdsToRetrieve = Object.keys(imageIds);
        let images$ = combineLatest(imageIdsToRetrieve.map(imageId => this.getImage(+imageId)));
        images$.pipe(
        // Convert the list of images ina dictionary of images indexed by image id.
        map(imageArray => {
            let imageDict = {};
            imageArray.forEach(image => imageDict[image.id] = image);
            return imageDict;
        }))
            //Once we have all images let's replace iamge tokens.
            .subscribe(imageDict => this.replaceImageTokensInArticles(imageDict, imageIds, articles), error => this.processError(error));
        return article;
    }
    /**
     * Replaces all image tokens in all articles.
     *
     * @param images: Dictionary with images uesd to replace tokens.
     * @param imageIds: Dictionary with articles list that use each image.
     * @param articles: Dictionary with article objects. Process is done inthose objects.
     *
     */
    replaceImageTokensInArticles(images, imageIds, articles) {
        Object.keys(imageIds).forEach(imageId => {
            Object.keys(imageIds[imageId]).forEach(articleId => {
                let article = articles[+articleId];
                let image = images[+imageId];
                article.text = article.text.replace(`[EXT=${imageId}]`, image.html);
            });
        });
    }
    processRawArticleRecursive(rawArticle, imageIds, articles) {
        let article = new Article(rawArticle);
        articles[article.id] = article;
        // Process children if any.
        if (Array.isArray(rawArticle.children)) {
            article.children = rawArticle.children.map((articleChild) => this.processRawArticleRecursive(articleChild, imageIds, articles));
        }
        this.processRefTokens(rawArticle, article);
        // Store article.
        this.storage.putArticle(article);
        // Collect image ids.
        if (Array.isArray(rawArticle.ext)) {
            rawArticle.ext.forEach((token) => this.processExtToken(token, imageIds, article.id));
        }
        return article;
    }
    processRefTokens(rawArticle, article) {
        // Start with raw text value in text. Then process text.
        article.text = article.rawText;
        if (Array.isArray(rawArticle.ref)) {
            rawArticle.ref.forEach((refToken) => this.processRefToken(refToken, article));
        }
    }
    processRefToken(refToken, article) {
        article.text = article.text.replace(`[REF=${refToken.id}]`, '<em><a data-link ="' + refToken.id + '">' + refToken.label + '</a></em>');
    }
    processExtToken(extToken, imageIds, id) {
        if (typeof imageIds[extToken] == 'undefined') {
            imageIds[extToken] = {};
        }
        // Remember this image is used in this article.
        imageIds[extToken][id] = id;
    }
    // Image methods.
    /////////////////
    /**
     * Image.
     */
    getImage(id) {
        // Load index item from cache.
        let image = this.storage.getImage(id);
        if (image) {
            this.logger.log(`Return image "${id}" from storage`, image);
            return of(image);
        }
        else {
            // Load image from api.
            this.logger.log(`Fetch image "${id}" from server`);
            return this.fetcher.getImage(id).pipe(map(rawImage => this.processRawImage(rawImage)));
        }
    }
    processRawImage(rawImage) {
        let image = new RBImage(rawImage);
        this.storage.putImage(image);
        return image;
    }
    // Search methods.
    //////////////////
    /**
     * Search.
     */
    search(text, page) {
        return this.fetcher.search(text, page).pipe(tap(x => this.logger.error('TAP', x)), map(rawSearchResults => this.processRawSearchResults(rawSearchResults)));
    }
    processRawSearchResults(rawSearchResults) {
        return new SearchResults(rawSearchResults);
    }
    processError(error) {
        this.logger.error(`Error in ${this.constructor.name} (F4E6B011)`);
    }
    // Flying page methods.
    ///////////////////////
    getFlyingPage(id) {
        // Load index item from cache.
        let article = this.storage.getFlyingPage(id);
        if (article) {
            this.logger.log(`Return article "${id}" from storage`, article);
            return of(article);
        }
        else {
            // Load article from api.
            this.logger.log(`Fetch index "${id}" from server`, article);
            return this.fetcher.getFlyingPage(id).pipe(map(rawArticle => this.processRawFlyingPage(rawArticle)));
        }
    }
    processRawFlyingPage(rawFlyingPage) {
        let flyingPage = new Article(rawFlyingPage);
        this.storage.putFlyingPage(flyingPage);
        return flyingPage;
    }
    getInterpretations() {
        return this.fetcher.getInterpretations();
    }
}
DataService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function DataService_Factory() { return new DataService(i0.ɵɵinject(i1.StorageService), i0.ɵɵinject(i2.FetchService), i0.ɵɵinject(i3.LoggerService)); }, token: DataService, providedIn: "root" });
